import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "../../components/seo/SeoHead";
import SeoMarketingShell from "../../components/seo/SeoMarketingShell";

const faqItems = [
  {
    q: "Do you deliver to the door only?",
    a: "No. Bellhopt Concierge is built for in-home stocking and basic organization when access is provided.",
  },
  {
    q: "What is the minimum lead time?",
    a: "24 hours. This protects quality and prevents rushed, inconsistent arrivals.",
  },
  {
    q: "Who is this for?",
    a: "Guests who want a smoother arrival, and hosts or property managers who want a consistent arrival-ready standard.",
  },
  {
    q: "What if items are out of stock?",
    a: "We follow substitution preferences when available. If a substitution is unclear, we prioritize guest-friendly alternatives that match the intent of the item.",
  },
  {
    q: "Is this limited to one city?",
    a: "Bellhopt supports major short-term rental markets across the US. Availability can vary by market and operational coverage.",
  },
  {
    q: "Do you support large portfolios?",
    a: "Yes. Bellhopt supports portfolios of any size, including 1,000+ homes.",
  },
];

const GLASS_CARD =
  "rounded-3xl border border-[#E5E7EB] bg-white/95 backdrop-blur-2xl shadow-[0_24px_80px_rgba(15,23,42,0.12)]";
const GLASS_CARD_SOFT =
  "rounded-3xl border border-[#E5E7EB] bg-white/92 backdrop-blur-2xl shadow-[0_18px_60px_rgba(15,23,42,0.10)]";
const PRIMARY_CTA =
  "rounded-full px-7 py-3 text-white text-[12px] font-semibold tracking-[0.12em] uppercase bg-gradient-to-r from-[#FF4D4D] via-[#FF6B6B] to-[#FF4D4D] shadow-[0_14px_40px_rgba(248,113,113,0.45)] hover:shadow-[0_18px_54px_rgba(248,113,113,0.55)] transition-all";
const SECONDARY_CTA =
  "rounded-full px-7 py-3 text-[#111827] text-[12px] font-semibold tracking-[0.08em] uppercase border border-[#E5E7EB] bg-white hover:bg-[#F9FAFB] transition-all";

function Section({ id, title, children }) {
  return (
    <section id={id} className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
      <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">{title}</h2>
      <div className="mt-4 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">{children}</div>
    </section>
  );
}

function VacationRentalGroceryDeliveryPage() {
  const title = "Vacation Rental Grocery Delivery | Delivered, Stocked, Organized | Bellhopt";
  const description =
    "Vacation rental grocery delivery handled by Bellhopt Concierge. Not just door drop-off. Delivered, stocked, and organized for an arrival-ready home. 24-hour lead time.";
  const canonical = "https://bellhopt.com/vacation-rental-grocery-delivery/";

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Vacation Rental Grocery Delivery, Stocked And Organized For Arrival",
      url: canonical,
      description,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://bellhopt.com/" },
        { "@type": "ListItem", position: 2, name: "Vacation Rental Grocery Delivery", item: canonical },
      ],
    },
  ];

  return (
    <SeoMarketingShell>
      <SeoHead
        title={title}
        description={description}
        canonical="/vacation-rental-grocery-delivery/"
        robots="index,follow"
        ogTitle="Vacation Rental Grocery Delivery, Done For You"
        ogDescription="Groceries delivered, stocked, and organized by Bellhopt Concierge for a 5-star arrival. 24-hour lead time required."
        structuredData={structuredData}
      />

      <main className="w-[90%] max-w-[1100px] m-auto pt-24 pb-10 mobile:w-[95%]">
        <section className={`${GLASS_CARD} p-8 mobile:p-5`}>
          <h1 className="font-segoe font-semibold text-[42px] mobile:text-[30px] text-[#111827] leading-tight">
            Vacation Rental Grocery Delivery, Stocked And Organized For Arrival
          </h1>
          <p className="mt-4 text-[20px] mobile:text-[17px] text-[#6B7280] leading-8">
            Bellhopt Concierge does more than deliver groceries to the door. We deliver, stock, and organize the home so guests walk into a ready-to-live-in space.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href="https://host.bellhopt.com/signup" className={PRIMARY_CTA}>
              Get Started With Bellhopt
            </a>
            <Link to="/for/property-managers" className={SECONDARY_CTA}>
              For Property Managers
            </Link>
          </div>
          <p className="mt-4 text-[13px] text-[#6B7280]">
            24-hour lead time required. Built for portfolios of any size, including 1,000+ homes.
          </p>
        </section>

        <div className="mt-6 space-y-6">
          <Section id="why-not-enough" title='Why "Delivery" Is Not Enough'>
            <p>Most grocery delivery ends at the doorstep. That is helpful, but it does not create a premium arrival.</p>
            <p className="mt-4">A true arrival-ready experience means:</p>
            <ul className="list-disc pl-6 mt-2">
              <li>Groceries are already inside, chosen from 20,000+ products</li>
              <li>The fridge and pantry are stocked where items belong</li>
              <li>The kitchen looks prepared, not cluttered</li>
              <li>Guests do not start their trip with errands</li>
            </ul>
            <p className="mt-4">Bellhopt is designed for that standard.</p>
          </Section>

          <Section id="what-you-get" title="What You Get With Bellhopt Concierge">
            <p>Bellhopt Concierge can support:</p>
            <ul className="list-disc pl-6 mt-2">
              <li>Grocery delivery to your vacation rental</li>
              <li>In-home stocking when access is provided</li>
              <li>Basic organization so the home feels intentionally prepared</li>
              <li>Coordination aligned to your property access method</li>
            </ul>
            <p className="mt-4">
              If you are managing guest experience at scale, consistency matters more than "one-off" deliveries.
            </p>
          </Section>

          <Section id="how-it-works" title="How It Works">
            <ol className="list-decimal pl-6 space-y-4">
              <li>
                <strong>Onboard your property</strong>
                <p>Hosts and property managers onboard via integration, or manually if needed. Once a property is set up, we keep property details up to date so our operations can dispatch and handle logistics reliably.</p>
              </li>
              <li>
                <strong>Guests shop ahead of time</strong>
                <p>Guests browse over 20,000 products and order exactly what they want through a pre-arrival shopping experience.</p>
              </li>
              <li>
                <strong>Bellhopt Concierge executes</strong>
                <p>We shop, deliver, stock, and organize based on the order and property instructions.</p>
              </li>
              <li>
                <strong>Arrival, upgraded</strong>
                <p>Guests arrive to a home that feels prepared from the first hour.</p>
              </li>
            </ol>
          </Section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
              Make Arrival Effortless
            </h2>
            <p className="mt-4 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
              Pre-arrival groceries, stocked and organized by Bellhopt Concierge. 24-hour lead time required.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="https://host.bellhopt.com/signup" className={PRIMARY_CTA}>Get Started With Bellhopt</a>
              <Link to="/for/property-managers" className={SECONDARY_CTA}>For Property Managers</Link>
            </div>
          </section>

          <Section id="built-for-pms" title="Built For Property Managers">
            <p>Guest experience enhancements often fail because they add work, create exceptions, and increase support tickets.</p>
            <p className="mt-4">Bellhopt is designed to reduce friction:</p>
            <ul className="list-disc pl-6 mt-2">
              <li>Standardized execution across markets</li>
              <li>Clear policies that protect consistency</li>
              <li>A concierge layer that supports guest experience without requiring your team to coordinate every detail</li>
            </ul>
          </Section>

          <Section id="lead-time" title="The 24-Hour Lead Time Policy">
            <p>
              To protect the guest experience and ensure reliable execution, Bellhopt requires a minimum 24-hour lead time for grocery delivery and stocking orders.
            </p>
            <p className="mt-4">
              Need exceptions for enterprise agreements? That is handled commercially with clear terms and rush rules.
            </p>
          </Section>

          <Section id="faq" title="FAQ">
            <div className="space-y-3">
              {faqItems.map((item) => (
                <details key={item.q} className="rounded-2xl border border-[#E5E7EB] p-4 bg-white/90 open:bg-[#F9FAFB]">
                  <summary className="cursor-pointer font-segoe font-semibold text-[18px] text-[#111827]">{item.q}</summary>
                  <p className="mt-3 text-[16px] text-[#6B7280] leading-7">{item.a}</p>
                </details>
              ))}
            </div>
          </Section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
              Ready For An Arrival-Ready Standard?
            </h2>
            <p className="mt-3 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
              Deliver, stock, and organize for every guest arrival.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="https://host.bellhopt.com/signup" className={PRIMARY_CTA}>Get Started</a>
              <a href="mailto:sales@bellhopt.com" className={SECONDARY_CTA}>Talk To Sales</a>
            </div>
          </section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">Related Pages</h2>
            <ul className="mt-4 list-disc pl-6 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
              <li><Link className="text-primary" to="/pre-arrival-groceries">Pre-arrival groceries</Link></li>
              <li><Link className="text-primary" to="/airbnb-grocery-stocking">Airbnb grocery stocking</Link></li>
              <li><Link className="text-primary" to="/for/property-managers">For property managers</Link></li>
              <li><Link className="text-primary" to="/integrations">Integrations</Link></li>
              <li><Link className="text-primary" to="/how-it-works">How it works</Link></li>
              <li><Link className="text-primary" to="/pricing">Pricing</Link></li>
            </ul>
          </section>
        </div>
      </main>
    </SeoMarketingShell>
  );
}

export default VacationRentalGroceryDeliveryPage;
